/**
 * CockpitService — API client for the Cockpit AI agent backend.
 *
 * Handles session CRUD and SSE-streamed chat responses.
 * All methods require a valid Keycloak JWT token.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export interface CockpitSession {
  id: string;
  user_id: string;
  org_id: string;
  agent_type: string;
  title: string;
  message_count?: number;
  created_at: string;
  updated_at: string;
}

export interface CockpitMessage {
  id: string;
  session_id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

export interface SSEEvent {
  type:
    | 'text_delta'
    | 'reasoning_delta'
    | 'tool_call'
    | 'tool_result'
    | 'block_start'
    | 'block_stop'
    | 'message_stop'
    | 'metadata'
    | 'error';
  text?: string;
  block_type?: string;
  session_id?: string;
  error?: string;
  /** Full response text (may appear in message_stop) */
  response?: string;
  /** Tool names used (may appear in message_stop — ignored by frontend) */
  toolsUsed?: string[];
  usage?: { input_tokens: number; output_tokens: number };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getBaseUrl(): string {
  // In development, use empty string so requests go through the Vite dev proxy
  if (import.meta.env.DEV) return '';
  return import.meta.env.VITE_COCKPIT_API_URL || 'https://chatapi.enlixy-test.com';
}

function authHeaders(token: string): Record<string, string> {
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`Cockpit API error ${response.status}: ${body || response.statusText}`);
  }
  return response.json();
}

// ─── Session API ─────────────────────────────────────────────────────────────

export async function createSession(
  token: string,
  title?: string,
): Promise<CockpitSession> {
  const response = await fetch(`${getBaseUrl()}/api/sessions`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ use_case: 'cockpit', title }),
  });
  return handleResponse<CockpitSession>(response);
}

export async function listSessions(token: string): Promise<CockpitSession[]> {
  const response = await fetch(`${getBaseUrl()}/api/sessions?agent_type=cockpit`, {
    headers: authHeaders(token),
  });
  return handleResponse<CockpitSession[]>(response);
}

export async function getSession(
  token: string,
  sessionId: string,
): Promise<CockpitSession> {
  const response = await fetch(`${getBaseUrl()}/api/sessions/${encodeURIComponent(sessionId)}`, {
    headers: authHeaders(token),
  });
  return handleResponse<CockpitSession>(response);
}

export async function getMessages(
  token: string,
  sessionId: string,
): Promise<CockpitMessage[]> {
  const response = await fetch(
    `${getBaseUrl()}/api/sessions/${encodeURIComponent(sessionId)}/messages`,
    { headers: authHeaders(token) },
  );
  return handleResponse<CockpitMessage[]>(response);
}

export async function renameSession(
  token: string,
  sessionId: string,
  title: string,
): Promise<void> {
  const response = await fetch(
    `${getBaseUrl()}/api/sessions/${encodeURIComponent(sessionId)}`,
    {
      method: 'PUT',
      headers: authHeaders(token),
      body: JSON.stringify({ title }),
    },
  );
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`Cockpit API error ${response.status}: ${body || response.statusText}`);
  }
}

export async function deleteSession(
  token: string,
  sessionId: string,
): Promise<void> {
  const response = await fetch(
    `${getBaseUrl()}/api/sessions/${encodeURIComponent(sessionId)}`,
    {
      method: 'DELETE',
      headers: authHeaders(token),
    },
  );
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`Cockpit API error ${response.status}: ${body || response.statusText}`);
  }
}

// ─── SSE Chat Stream ─────────────────────────────────────────────────────────

/**
 * Send a chat message and stream SSE events.
 *
 * Yields parsed SSEEvent objects. The caller can consume these
 * to build the assistant's response incrementally.
 *
 * The AbortSignal allows cancellation from the UI.
 */
export async function* streamChat(
  token: string,
  sessionId: string,
  message: string,
  abortSignal?: AbortSignal,
): AsyncGenerator<SSEEvent> {
  const response = await fetch(
    `${getBaseUrl()}/api/sessions/${encodeURIComponent(sessionId)}/chat`,
    {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({ message }),
      signal: abortSignal,
    },
  );

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`Cockpit chat error ${response.status}: ${body || response.statusText}`);
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data: ')) continue;

        const data = trimmed.slice(6);
        if (data === '[DONE]') return;

        try {
          const parsed = JSON.parse(data) as SSEEvent;
          console.debug('🤖 SSE event:', parsed.type, parsed);
          yield parsed;
        } catch {
          // Skip malformed events
          console.warn('Cockpit: skipping malformed SSE event:', data);
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
